import { Alert, Button, Card, Col, Descriptions, Flex, Input, InputNumber, Row, Select, Space, Statistic, Table, Typography } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../lib/toast';
import { hasBillingAccess } from '../access';
import * as api from '../api';
import { formatDate, formatINR, todayISO } from '../format';
import { useZenBootstrap, zenKeys } from '../hooks';
import { orderStatus } from '../status';
import type { LedgerEntry, WorkOrder } from '../types';
import { DueText, EmptyState, OrderNumber, StatusPill, TableSkeleton } from '../ui';

const { Text } = Typography;

const LEDGER_TYPE_LABEL: Record<LedgerEntry['type'], string> = {
  opening_balance: 'Opening balance',
  invoice: 'Invoice',
  payment: 'Payment',
  adjustment: 'Adjustment',
};

export function AccountsPage() {
  const { data } = useZenBootstrap();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [entry, setEntry] = useState({ type: 'opening_balance' as 'opening_balance' | 'payment' | 'adjustment', amount: 0, date: todayISO(), reference: '', notes: '' });
  const [posting, setPosting] = useState(false);
  const selectedClient = data?.clients.find((client) => client.id === clientId);
  const ledger = useQuery({
    queryKey: clientId ? zenKeys.ledger(clientId) : ['zen', 'ledger', 'none'],
    queryFn: ({ signal }) => api.fetchLedger(clientId!, signal),
    enabled: Boolean(clientId && selectedClient),
  });
  if (!data) return <TableSkeleton />;
  const canAdjust = hasBillingAccess(data.currentUser);
  const customerOptions = data.clients.map((client) => ({ value: client.id, label: `${client.name} · ${client.phone}` }));

  if (!clientId) {
    const pending = data.workOrders.filter((order) => !order.accountsApproved && order.status === 'active' && !order.deletedAt);
    const approved = data.workOrders.filter((order) => order.accountsApproved && !order.deletedAt);
    const orderColumns = [
      {
        title: 'Order',
        key: 'order',
        render: (_: unknown, order: WorkOrder) => <Link to={`/work-orders/${order.id}`}><OrderNumber>{order.orderNumber}</OrderNumber></Link>,
      },
      { title: 'Customer', dataIndex: 'customerName' },
      { title: 'Status', key: 'status', render: (_: unknown, order: WorkOrder) => <StatusPill status={orderStatus(order)} /> },
      { title: 'Due', key: 'due', render: (_: unknown, order: WorkOrder) => <DueText date={order.deliveryDate} done={order.status !== 'active'} />, width: 120 },
      { title: 'Items', key: 'items', align: 'right' as const, render: (_: unknown, order: WorkOrder) => order.items.length, width: 70 },
      {
        title: 'Invoice',
        key: 'invoice',
        render: (_: unknown, order: WorkOrder) => order.invoice
          ? <span className="ua-mono">{order.invoice.number}</span>
          : order.accountsApproved ? <Text type="secondary">Before invoicing</Text> : <Link to={`/work-orders/${order.id}`}>Record invoice</Link>,
      },
      {
        title: 'Amount',
        key: 'amount',
        align: 'right' as const,
        render: (_: unknown, order: WorkOrder) => order.invoice ? formatINR(order.invoice.amount) : <Text type="secondary">—</Text>,
      },
    ];
    return (
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Card title={<span>To invoice <span className="ua-count">{pending.length}</span></span>}>
          <Table
            className="zen-responsive-table"
            rowKey="id"
            size="small"
            dataSource={pending}
            columns={orderColumns}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: <EmptyState compact title="Nothing waiting for an invoice" description="New work orders appear here as soon as Sales creates them." /> }}
          />
        </Card>
        <Card title={<span>Invoiced orders <span className="ua-count">{approved.length}</span></span>}>
          <Table className="zen-responsive-table" rowKey="id" size="small" dataSource={approved} columns={orderColumns} pagination={{ pageSize: 15, showSizeChanger: false }} scroll={{ x: 'max-content' }} />
        </Card>
        <Card title="Customer ledger">
          <Select
            showSearch
            optionFilterProp="label"
            onChange={(value) => navigate(`/accounts/${value}`)}
            placeholder="Search customers"
            options={customerOptions}
            style={{ width: 'min(100%, 380px)' }}
          />
        </Card>
      </Space>
    );
  }

  const balance = ledger.data?.balance ?? 0;
  return (
    <Row gutter={[16, 16]} align="top">
      <Col xs={24} xl={canAdjust && selectedClient ? 17 : 24}>
        <Card>
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Flex justify="space-between" align="center" gap={16} wrap>
              <Select
                showSearch
                optionFilterProp="label"
                value={clientId}
                onChange={(value) => navigate(`/accounts/${value}`)}
                placeholder="Search customers"
                options={customerOptions}
                className="zen-customer-select"
                style={{ width: 'min(100%, 320px)' }}
              />
              {ledger.data && (
                <Statistic
                  title={balance > 0 ? 'Balance due' : 'Balance'}
                  value={formatINR(balance)}
                  styles={{ content: { color: balance > 0 ? 'var(--ua-warning-text)' : undefined } }}
                />
              )}
            </Flex>
            {selectedClient && (
              <Descriptions className="zen-responsive-descriptions" size="small" column={{ xs: 1, md: 2 }} colon={false}>
                <Descriptions.Item label="Customer">{selectedClient.name}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selectedClient.phone ? <span className="ua-mono">{selectedClient.phone}</span> : 'Not given'}</Descriptions.Item>
                <Descriptions.Item label="Address" span={{ xs: 1, md: 2 }}>{selectedClient.address || 'Not given'}</Descriptions.Item>
              </Descriptions>
            )}
            {!selectedClient ? (
              <Alert type="error" showIcon title="Customer not found" description="Choose a customer from the list." />
            ) : ledger.isLoading ? (
              <TableSkeleton rows={6} />
            ) : (
              <Table
                className="zen-responsive-table"
                rowKey="id"
                size="small"
                dataSource={ledger.data?.entries ?? []}
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 20, responsive: true, showSizeChanger: false }}
                locale={{ emptyText: <EmptyState compact title="No ledger entries yet" description="Invoices and payments for this customer appear here." /> }}
                columns={[
                  { title: 'Date', dataIndex: 'date', render: (value: string) => formatDate(value) },
                  { title: 'Type', dataIndex: 'type', render: (value: LedgerEntry['type']) => LEDGER_TYPE_LABEL[value] ?? value },
                  { title: 'Reference', dataIndex: 'reference', render: (value: string) => value ? <span className="ua-mono">{value}</span> : <Text type="secondary">—</Text> },
                  {
                    title: 'Work order',
                    key: 'workOrderNumber',
                    render: (_, item) => item.workOrderId
                      ? <Link to={`/work-orders/${item.workOrderId}`}><OrderNumber>{item.workOrderNumber}</OrderNumber></Link>
                      : item.workOrderNumber || <Text type="secondary">—</Text>,
                  },
                  { title: 'By', dataIndex: 'createdByName' },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    align: 'right',
                    render: (value: number) => <span className={value < 0 ? 'ua-text-success' : undefined}>{formatINR(value)}</span>,
                  },
                ]}
              />
            )}
          </Space>
        </Card>
      </Col>
      {canAdjust && selectedClient && (
        <Col xs={24} xl={7}>
          <Card title="Add ledger entry">
            <Space orientation="vertical" size={8} style={{ width: '100%' }}>
              <Select value={entry.type} onChange={(value) => setEntry({ ...entry, type: value })} style={{ width: '100%' }} options={[
                { value: 'opening_balance', label: 'Opening balance' },
                { value: 'payment', label: 'Payment' },
                { value: 'adjustment', label: 'Adjustment' },
              ]} />
              <InputNumber value={entry.amount} onChange={(value) => setEntry({ ...entry, amount: Number(value) })} formatter={(value) => (value ? Number(value).toLocaleString('en-IN') : '')} parser={(value) => Number((value ?? '').replace(/[^\d.-]/g, ''))} prefix="₹" style={{ width: '100%' }} />
              <Input type="date" value={entry.date} onChange={(event) => setEntry({ ...entry, date: event.target.value })} />
              <Input value={entry.reference} onChange={(event) => setEntry({ ...entry, reference: event.target.value })} placeholder="Reference" />
              <Input.TextArea value={entry.notes} onChange={(event) => setEntry({ ...entry, notes: event.target.value })} placeholder="Reason or note" />
              <Button
                type="primary"
                disabled={!entry.amount}
                loading={posting}
                onClick={async () => {
                  setPosting(true);
                  try {
                    await api.addLedgerEntry({ clientId, ...entry });
                    await queryClient.invalidateQueries({ queryKey: zenKeys.ledger(clientId) });
                    toast.success(`${LEDGER_TYPE_LABEL[entry.type]} of ${formatINR(entry.amount)} added`);
                    setEntry({ type: 'opening_balance', amount: 0, date: todayISO(), reference: '', notes: '' });
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Couldn’t add the entry. Try again.');
                  } finally {
                    setPosting(false);
                  }
                }}
              >
                Add entry
              </Button>
            </Space>
          </Card>
        </Col>
      )}
    </Row>
  );
}
